// FeatureTable（v0.31.3）：**行内直编 + 展开行** —— 删掉底部编辑栏与「操作」列
//
// 用户反馈：「能不能把下面的编辑栏去掉……或者在特征栏那里直接编辑，操作按钮可以去掉」
// → 采纳「行内直编 + 展开行」，并把画布/表格下方的 FeatureDetail 通栏面板整体移除
//   （它挂在满屏 Flex 布局之下，选中任一行就会挤掉画布约 40% 高度、且每次点行都跳一次）。
//
// 交互契约（v0.31.3）：
//   · 单击行 ......... 选中该行（画布气球高亮 + 居中 + 表格滚动 + 跨页跳转）
//                      并**同时展开**低频字段；再点这一行 = 收起
//   · ↑ / ↓ ......... 选中上一行 / 下一行（画布跟着居中）；不抢输入框与下拉的方向键
//   · 单击编号徽标 ... 循环切换状态：待确认 → 已确认 → 异常 → 待确认
//                      （「已修改」由后端在字段被改后自动置，不进循环，避免误以为已复核）
//   · 双击「特征」名 . 改名（characteristic）
//   · 「要求/公差」... 该行已选中时单击即改 / 任意行双击 → 就地变输入框
//                      Enter / 失焦保存 · Esc 取消 · 空值写 null
//   · 展开行 ......... **特征ID**（只读，最前）/ 检测工具 / 精度 / 单位 / 上偏差 / 下偏差 /
//                      形位公差（勾选 + 符号 + 公差带 + Ø + 基准特征 + 材料）/
//                      **备注**（最后）—— **改动即生效**（失焦即保存），行尾只留「删除」；
//                      同一时刻只展开一行
//   · 勾选框 + 批量条 . 多选一键确认 / 排除 / 删除（保持不变）
//
// 字段落点：
//   「要求」→ nominal（GD&T 行 → gdt.zone）；「公差」→ **派生**（见 lib/annot.ts）
//   上/下偏差是结构化字段，只在展开行里改 —— 不从派生文本反解，避免把语义写歪。
//
// v0.31.3（2026-10-03，用户第二次反馈）四处改动：
//   ① 「展开行改上下偏差、公差列不跟着变」→ 公差列改由 `toleranceDisplay()` 派生：
//      **结构化偏差优先**，识别阶段顺手塞的 tolerance_text 降为兜底
//      （旧库实测存的是 `0.2/-0`、`0/-0` 这类带 `-0` 的粗糙文本，它以前排在最前，
//       于是改偏差看起来"不生效"）。口径全部收进 lib/annot.ts。
//   ② 单边公差（0/-0.1、+0.2/0）怎么输入 → 只填一侧、另一侧留空即按 0 处理；
//      展开行给出「实时预览 + 单边提示」，不再让人猜。
//   ③ 特征ID 提到展开行最前、备注挪到最后（用户要求）。
//   ④ 新增「形位公差」整块：勾选即把本行标记为 GD&T（kind，需后端放行），
//      符号改成 ASME Y14.5-2018 图 6-1 的标准符号下拉，基准改成「基准特征」编辑器
//      （字母 + 实体条件，顺序即基准顺序）。
//
// v0.31.5（2026-10-04）：
//   ① 「量具」标签 → **「检测工具」**；
//   ② 候选表新增 目视 / 针规 / 膜厚仪 / 扭力 / 推拉力，「塞规」→「塞尺」；
//   ③ 候选表**收敛到 lib/annot.ts 的 `METHOD_OPTS` 单一出处** —— 它原先是本文件与
//      balloonStyle.ts 各一份（后者的 `METHOD_OPTS_SHARED` 根本没人引用），
//      两份分叉后改一份另一份不跟，就是「改了没反应」的经典成因。
//
// v0.58（2026-10-16，用户诉求「4# 尺寸 4*R5 输不进去」）：
//   「数量」不是新字段 —— `features.qty` 早就有、识别器也在写（图纸原文 `4X R5.0`
//   解析成 qty=4），但它**从未在界面上出现过**：请求体 schema 也没放行，所以既看不见
//   也改不了，用户只好试图把 `4*R5` 塞进「要求」列（绑的是数值 nominal）而被拦下。
//   三处改动，**不改存储口径**（nominal 仍是数值，否则 is_dim / 上下限 /
//   测量报告 `=B+T` 公式整条派生链会断）：
//     ① 展开行第 1 行新增「数量（处）」输入框 → 写 qty；
//     ② 「要求」列**显示层**合成 `4×5`（编辑框里仍是纯数值，见 `reqDisplay`）；
//     ③ 「要求」列接受数量写法 `4*R5` / `4X5` / `4×R5` → 一次提交 qty + nominal。
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { patchFeature, setFeatureStatus, batchSetFeatureStatus, mergeFeatures } from '../api/client';
import type { Datum, Feature, FeatureStatus } from '../types';
import { STATUS_CN, STATUS_HINT, STATUS_COLORS } from '../types';
import { useFeatureDelete } from '../lib/useFeatureDelete';
import { isTypingTarget } from '../lib/keys';
import {
  CC_SC_OPTS, DATUM_COND_OPTS, GDT_SYMBOLS, MATERIAL_OPTS,
  characteristicZh, fmtNum, gdtZoneText, parseDev, parseQtyNominal, symbolAllowsDatum,
  symbolAllowsMmc, symbolGlyph, symbolToZh, toleranceDisplay, unilateralHint,
  withModifierSuffix,
} from '../lib/annot';
import { useUI } from '../store/ui';
// v0.61：`METHOD_OPTS` 不再在这里直接引用 —— 检测工具候选改由后台清单驱动
// （`useOptionValues('method')`，内部再用 `annot.ts` 的 METHOD_OPTS 做出厂兜底）。
import { useOptionValues } from '../store/options';

interface Props {
  drawingId: number;
  features: Feature[];
  onChanged: () => void;
  /** v0.47：只读角色（查看者 / 检验员对"特征本身"是只读的）。
   *  ★ 这里只是**界面**层的收敛；真正的拦截在后端权限矩阵
   *    （`PATCH /api/drawing/{id}/feature/{pk}` 要求 engineer）。 */
  readOnly?: boolean;
}

/** 筛选下拉的取值：'all' | 各状态 | 'review'（仅待复核）。 */
type FilterKey = 'all' | FeatureStatus | 'review';
/** 行内可直编的单元格：特征名 / 要求 / 公差。 */
type EditKey = 'name' | 'req' | 'tol';

const STATUS_ORDER: FeatureStatus[] = ['AI_FOUND', 'CONFIRMED', 'MODIFIED', 'REJECTED'];
// 徽标点击的循环顺序：刻意**跳过 MODIFIED** —— 它是后端在字段被改后自动置的中间态，
// 手动切进去没有意义，反而会让人误以为「已经复核过了」。
const STATUS_CYCLE: FeatureStatus[] = ['AI_FOUND', 'CONFIRMED', 'REJECTED'];

// ⚠️ 检测工具（原「量具」）候选**不在这里** —— 唯一出处是 `lib/annot.ts` 的
//    `METHOD_OPTS`。曾经本文件与 balloonStyle.ts 各存一份，分叉后改一份另一份不跟。
const UNIT_OPTS = ['mm', 'inch', '°'];

function cycleOf(s: FeatureStatus): FeatureStatus {
  const i = STATUS_CYCLE.indexOf(s);
  return i < 0 ? 'CONFIRMED' : STATUS_CYCLE[(i + 1) % STATUS_CYCLE.length];
}

/** number | null → 输入框可用的字符串（0 要保留，所以不能用 `v || ''`）。 */
function numStr(v: number | null | undefined): string {
  return v != null ? String(v) : '';
}

/**
 * 草稿输入：本地态 + **失焦 / Enter 提交** + Esc 回退。展开行里所有文本框共用。
 *
 * 两个守卫（都是踩过的坑，别删）：
 *   · `foc` —— 提交后会 refetch，外部 value 变化不能让正在输入的框被外部值覆盖；
 *   · `skipRef` —— Esc 先 setV(旧值) 再 blur，若不标记跳过，blur 会拿闭包里的
 *     旧草稿（用户刚敲的）提交上去 —— Esc 反而成了保存。
 */
function useDraft(value: string, onCommit: (v: string) => void) {
  const [v, setV] = useState(value);
  const [foc, setFoc] = useState(false);
  const skipRef = useRef(false);
  useEffect(() => { if (!foc) setV(value); }, [value, foc]);
  return {
    value: v,
    onFocus: () => setFoc(true),
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => setV(e.target.value),
    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
      else if (e.key === 'Escape') {
        skipRef.current = true; setV(value); e.currentTarget.blur();
      }
    },
    onBlur: () => {
      setFoc(false);
      if (skipRef.current) { skipRef.current = false; return; }
      if (v !== value) onCommit(v);
    },
  };
}

// ---------------------------------------------------------------- 行内单元格编辑器
// 只做一件事：把一段文本编好并交出去。保存/取消都只触发一次 —— Enter 会连带
// 触发 blur，没有 doneRef 守卫就会提交两次（第二次用旧值覆盖第一次）。
/* v0.73：删掉 `align` 参数 —— 它唯一的取值是 `'right'`，而整表现在居中；
   留着它就会有人再传一个右对齐，把「表头与单元格同一种对齐」这条口径破坏掉
   （v0.50 在 SIP 表上就是这个错）。对齐唯一出处 = `.cell-edit` 的 `text-align`。 */
function CellEditor({ initial, placeholder, onCommit, onCancel }: {
  initial: string;
  placeholder?: string;
  onCommit: (v: string) => void;
  onCancel: () => void;
}) {
  const [v, setV] = useState(initial);
  const doneRef = useRef(false);
  const finish = (ok: boolean) => {
    if (doneRef.current) return;
    doneRef.current = true;
    if (ok) onCommit(v); else onCancel();
  };
  return (
    <input className="cell-edit"
           value={v}
           placeholder={placeholder || undefined}
           autoFocus
           onFocus={(e) => e.currentTarget.select()}
           onChange={(e) => setV(e.target.value)}
           onKeyDown={(e) => {
             if (e.key === 'Enter') { e.preventDefault(); finish(true); }
             else if (e.key === 'Escape') { e.preventDefault(); finish(false); }
           }}
           onBlur={() => finish(true)}
           onClick={(e) => e.stopPropagation()}
           onDoubleClick={(e) => e.stopPropagation()}
           onMouseDown={(e) => e.stopPropagation()} />
  );
}

// ---------------------------------------------------------------- 展开行里的字段
function FxField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="fx-f">
      <span className="fx-lbl">{label}</span>
      {children}
    </label>
  );
}

/**
 * 文本框 / 数字框。**失焦或 Enter 即提交**，所以展开行里没有「保存」按钮。
 * 草稿与守卫逻辑见 `useDraft`。
 */
function FxText({ label, value, onCommit, type = 'text', width, placeholder, title,
                  readOnly = false }: {
  label: string;
  value: string;
  onCommit: (v: string) => void;
  type?: string;
  width?: number;
  placeholder?: string;
  title?: string;
  /** v0.47：只读角色。渲染成等高的静态文本（**不是 disabled 输入框** ——
   *  置灰的输入框仍然两三次点击就能改，而"看起来能改但改不了"最容易被当成卡死）。 */
  readOnly?: boolean;
}) {
  const draft = useDraft(value, onCommit);
  if (readOnly) return <FxReadOnly label={label} value={value} title={title} />;
  return (
    <FxField label={label}>
      <input className="cell-input fx-in" type={type} placeholder={placeholder}
             title={title}
             style={width ? { width } : undefined}
             {...draft} />
    </FxField>
  );
}

function FxSelect({ label, value, opts, onCommit, readOnly = false }: {
  label: string; value: string; opts: string[]; onCommit: (v: string) => void;
  readOnly?: boolean;
}) {
  // 后端可能给出不在候选表里的检测工具名（历史数据/表外工具）→ 补进选项，否则
  // 受控 select 会显示成第一项，等于静默改值。
  const list = opts.includes(value) ? opts : [value, ...opts];
  if (readOnly) return <FxReadOnly label={label} value={value} />;
  return (
    <FxField label={label}>
      <select className="cell-input fx-in" value={value}
              onChange={(e) => onCommit(e.target.value)}>
        {list.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </FxField>
  );
}

function FxCheck({ label, checked, onCommit, readOnly = false }: {
  label: string; checked: boolean; onCommit: (v: boolean) => void;
  readOnly?: boolean;
}) {
  if (readOnly) {
    return (
      <label className="fx-f fx-f-check">
        <span className="fx-lbl">{label}</span>
        <span className="cell-ro fx-ro">{checked ? '是' : '否'}</span>
      </label>
    );
  }
  return (
    <label className="fx-f fx-f-check">
      <input type="checkbox" checked={checked}
             onChange={(e) => onCommit(e.target.checked)} />
      <span className="fx-lbl">{label}</span>
    </label>
  );
}

/** 只读字段：展示不可编辑的内部标识，用与输入框等高的样式保证排版不跳。 */
function FxReadOnly({ label, value, title }: {
  label: string; value: string; title?: string;
}) {
  return (
    <FxField label={label}>
      <span className="cell-ro fx-ro" title={title}>{value || '—'}</span>
    </FxField>
  );
}

/**
 * 形位公差符号选择器（v0.31.3）：从 ASME Y14.5-2018 图 6-1 的在册符号里选，
 * 不再让人手打符号 —— 手打必然出现「位置度写成 ⊕ 还是 ⌖」「平面度写成 □」这类分歧，
 * 而且 `gdt_features.symbol` 里一旦写进认不出的字符，报告就只剩一个乱码。
 *
 * 选项文案 = `字形 + 中文名`，右侧按标准补「需基准 / 可用 ⓂⓁ」的提示。
 * 库里若已是本表认不出的旧字符（识别产出），补一项原样列出，避免受控 select 静默改值。
 */
function FxSymbolSelect({ value, onCommit, readOnly = false }: {
  value: string; onCommit: (v: string | null) => void;
  readOnly?: boolean;
}) {
  const known = GDT_SYMBOLS.some((s) => s.code === value);
  const legacy = !!value && !known;
  if (readOnly) {
    return <FxReadOnly label="符号" value={value ? `${value} ${symbolToZh(value)}` : '— 未定 —'} />;
  }
  return (
    <FxField label="符号">
      <select className="cell-input fx-in fx-sym" value={value}
              title="ASME Y14.5-2018 图 6-1 的几何特征符号；⏤直线度 ▱平面度 ○圆度 ⌭圆柱度 ⌒线轮廓度 ⌓面轮廓度 ∥平行度 ⊥垂直度 ∠倾斜度 ⊕位置度 ↗圆跳动 ↗↗全跳动"
              onChange={(e) => onCommit(e.target.value || null)}>
        <option value="">— 未定 —</option>
        {legacy && <option value={value}>{value}（识别原值）</option>}
        {GDT_SYMBOLS.map((s) => (
          // 选项文案保持短（v0.31.4 起不再挂「2018 已取消」后缀，见 styles.css）；
          // 废弃信息改挂 `title`：既不会撑宽下拉，又能被前端回归门检到 ——
          // `verify_ui_annot_e2e.mjs` 断言这两个符号**必须仍在册且带废弃标记**，
          // 否则「被悄悄删掉」这件事没有任何地方会发现。
          <option key={s.code} value={s.code}
                  title={s.deprecated
                    ? `${s.en} · ASME Y14.5-2018 已取消（仅供旧图 / GB/T 1182 如实录入）`
                    : s.en}>
            {s.code} {s.zh}
          </option>
        ))}
      </select>
    </FxField>
  );
}

/**
 * 基准特征编辑器（v0.31.3，用户要求「增加……基准特征」）。
 *
 * 特征控制框里跟在公差带后面的字母就是基准（如 `⊕ | ⌀0.2 | A | B Ⓜ | C`），
 * **顺序即基准顺序 1/2/3**，所以这里按顺序渲染成带序号的格子，而不是原来的
 * 逗号分隔文本框（文本框看不见顺序，也没处放实体条件）。
 *
 * 每个格子 = 序号 + 基准字母 + 实体条件（MMC/LMC/RFS）。
 * 字母走草稿输入（失焦/Enter 提交，避免每敲一个字就 PATCH 一次刷屏 + 抖动）；
 * 条件是离散选择，直接提交。增删立即提交整个有序数组。
 */
function DatumEditor({ datums, symbol, onCommit, readOnly = false }: {
  datums: Datum[];
  symbol: string | null | undefined;
  onCommit: (d: Datum[]) => void;
  readOnly?: boolean;
}) {
  const needDatum = symbolAllowsDatum(symbol);
  const normalize = (list: Datum[]): Datum[] =>
    list.map((d, i) => ({ name: d.name, order: i + 1, condition: d.condition ?? null }));
  const setAt = (i: number, patch: Partial<Datum>) =>
    onCommit(normalize(datums.map((d, j) => (j === i ? { ...d, ...patch } : d))));
  const removeAt = (i: number) =>
    onCommit(normalize(datums.filter((_, j) => j !== i)));
  const add = () => {
    // 默认给「下一个没用过的字母」，省掉一次手输
    const used = new Set(datums.map((d) => (d.name || '').toUpperCase()));
    const next = 'ABCDEFGHJKLMNPQRSTUVWXYZ'.split('').find((c) => !used.has(c)) ?? 'A';
    onCommit(normalize([...datums, { name: next, order: datums.length + 1, condition: null }]));
  };
  // v0.47：只读角色 —— 展示"引用了哪些基准、什么实体条件"，但不给增删改的入口。
  if (readOnly) {
    return (
      <div className="fx-f fx-datum">
        <span className="fx-lbl">
          基准特征
          <span className="fx-lbl-sub">顺序即基准顺序 1/2/3</span>
        </span>
        <div className="fx-datum-row">
          {datums.length === 0 && <span className="fx-datum-empty">未引用基准</span>}
          {datums.map((d, i) => (
            <span className="fx-datum-chip" key={`${i}-${d.name}`}>
              <span className="fx-datum-ord">{i + 1}</span>
              <span className="cell-ro fx-ro">{d.name || '—'}</span>
              <span className="cell-ro fx-ro">{d.condition || 'RFS'}</span>
            </span>
          ))}
        </div>
      </div>
    );
  }
  return (
    <div className="fx-f fx-datum">
      <span className="fx-lbl">
        基准特征
        <span className="fx-lbl-sub">
          {needDatum ? '顺序即基准顺序 1/2/3' : '该符号为形状公差，标准上不引用基准'}
        </span>
      </span>
      <div className="fx-datum-row">
        {datums.length === 0 && <span className="fx-datum-empty">未引用基准</span>}
        {datums.map((d, i) => (
          <span className="fx-datum-chip" key={`${i}-${d.name}`}>
            <span className="fx-datum-ord">{i + 1}</span>
            <DatumName value={d.name ?? ''} onCommit={(v) => setAt(i, { name: v })} />
            <select className="cell-input fx-datum-cond"
                    title="基准实体条件修饰符"
                    value={d.condition ?? ''}
                    onChange={(e) => setAt(i, { condition: e.target.value || null })}>
              {DATUM_COND_OPTS.map((o) => (
                <option key={o.v} value={o.v}>{o.label}</option>
              ))}
            </select>
            <button type="button" className="fx-datum-del"
                    title="移除该基准" onClick={() => removeAt(i)}>×</button>
          </span>
        ))}
        <button type="button" className="fx-datum-add" onClick={add}>＋ 基准</button>
      </div>
    </div>
  );
}

/** 基准字母输入：1~3 字符，退化为空即代表该字未定（提交时保留，由用户自行删除）。 */
function DatumName({ value, onCommit }: { value: string; onCommit: (v: string) => void }) {
  const draft = useDraft(value, onCommit);
  return (
    <input className="cell-input fx-datum-name" maxLength={3} placeholder="A"
           title="基准字母（A/B/C…）" {...draft} />
  );
}

/** 展开行主体：所有低频字段的家。改动即刻提交，没有「保存字段」按钮。 */
function ExpandForm({ f, busy, onPatch, onNum, onDelete, readOnly = false }: {
  f: Feature;
  busy: boolean;
  onPatch: (patch: Record<string, any>) => void;
  onNum: (field: string, cur: number | null, raw: string, allowNull?: boolean) => void;
  onDelete: () => void;
  /** v0.47：只读角色 —— 字段照常展示，但全部不可编辑、没有删除按钮。 */
  readOnly?: boolean;
}) {
  const ro = readOnly;
  // v0.61：检测工具候选改由**后台清单**驱动（设置 · 量具与选项）。
  //   ★ 兜底仍走 `lib/annot.ts` 的 `METHOD_OPTS` —— 拉不到后台清单时下拉必须是
  //     有值的（受控 `<select>` 不能手输，空下拉等于连选都选不了）。
  const methodOpts = useOptionValues('method');
  const { showToast } = useUI();
  const gdtOn = f.kind === 'gdt';
  const sym = f.gdt?.symbol ?? '';
  // 「公差列会显示什么」实时预览 —— 与表格单元格共用同一个派生函数，避免两处口径漂移。
  const tolPreview = toleranceDisplay(f);
  const uni = unilateralHint(f.upper_dev, f.lower_dev);
  const materialLabel = MATERIAL_OPTS.find((o) => o.v === (f.gdt?.material_condition ?? ''))?.label
    ?? '—';
  return (
    <div className="fx-form">
      {(f.filter_status ?? 'ACCEPT') === 'REVIEW' && (
        <div className="fx-review">
          <span className="fx-review-msg">
            ⚠ 候选过滤判定为 <b>待人工复核</b>
            {f.filter_reason ? ` —— ${f.filter_reason}` : ''}
            {f.filter_region ? `（区域 ${f.filter_region}）` : ''}
            。这条标记<b>只是界面提示</b>：报告与 SIP 一直照常带上这一行，所以
            「保留」从来不是一个动作 —— 你要做的是把<b>标记本身</b>收口：
          </span>
          {/* v0.59：这一对按钮就是标记的出口。此前界面上只有"是就保留、不是就删除"
              这句文字，而"保留"在系统里**没有对应动作**（导出侧压根不判 filter_status，
              REVIEW 行本来就正常进报告）⇒ 用户按着提示走，却什么按钮也找不到，
              标记就一直挂着。现在把两种真实结论各配一个按钮。 */}
          {!ro && (
            <span className="fx-review-acts">
              <button type="button" className="btn sm act-cfm" disabled={busy}
                      title={'确认这是真实尺寸：清掉待复核标记（原因 / 区域一并清空）。\n'
                             + '不改尺寸值、也不改状态 —— 状态请点编号徽标切换。'}
                      onClick={() => onPatch({ filter_status: 'ACCEPT' })}>✓ 确认真实尺寸</button>
              <button type="button" className="btn sm act-del" disabled={busy}
                      title={'这不是尺寸（误识别）：删除该特征，气泡一并移除。\n'
                             + '与右下角「删除」是同一个操作，可按 Ctrl+Z 撤销。'}
                      onClick={onDelete}>✕ 不是尺寸</button>
            </span>
          )}
        </div>
      )}

      {/* 第 1 行：特征ID（只读，用户要求放最前）+ 检测工具 / 精度 / 单位 */}
      <div className="fx-line">
        {/* 特征ID 是内部标识（与气泡编号分离），批量删除后与编号同源补位。
            低频，所以不进表格列，只在这里露一眼 —— 否则它只剩 tooltip 一个入口。 */}
        <FxReadOnly label="特征ID" value={f.feature_id}
                    title="与气泡编号分离的内部标识；重排时两者同源补位" />
        {/* 数量（v0.58）：图纸上的 `4X R5.0` 里的 4。列早就有、识别器也在写，
            只是一直没有入口 —— 于是「这行是 4 处」在界面上等于不存在（用户诉求）。
            与「特征ID」同为**标注属性**，所以也放第 1 行。 */}
        <FxText label="数量（处）" type="number" width={54} readOnly={ro}
                value={numStr(f.qty)}
                title={'图纸上的数量前缀：`4X R5.0` 就填 4，只有一处填 1（默认）。\n'
                       + '也可以直接在「要求」列敲 4*R5 一次填好这两个字段。\n'
                       + '清空 = 1 处（库里的列不允许为空）。'}
                onCommit={(v) => {
                  const t = v.trim();
                  const n = t === '' ? 1 : Number(t);   // 清空 ⇒ 回到 1 处
                  if (!Number.isInteger(n) || n < 1) {
                    showToast('数量要是 ≥1 的整数（图纸写 4X 就填 4）', 'error');
                    return;
                  }
                  if (n !== f.qty) onPatch({ qty: n });
                }} />
        <FxSelect label="检测工具" value={f.method} opts={methodOpts} readOnly={ro}
                  onCommit={(v) => { if (v !== f.method) onPatch({ method: v }); }} />
        <FxText label="精度" type="number" width={58} value={numStr(f.precision)} readOnly={ro}
                onCommit={(v) => onNum('precision', f.precision ?? null, v, false)} />
        <FxSelect label="单位" value={f.unit} opts={UNIT_OPTS} readOnly={ro}
                  onCommit={(v) => { if (v !== f.unit) onPatch({ unit: v }); }} />
        {/* 特殊特性（v0.49）：AS9102 / PPAP / SIP「B 列」。
            与「检测工具」同类 —— 是**每行特性**的属性，尺寸行与形位公差行都适用，
            所以放在第 1 行而不是 GD&T 专有的那一段里。
            界面首项 `'—'` = 无特殊特性 ⇒ 提交 `null`（库里存量是 NULL，不写空串）。 */}
        <FxSelect label="特殊特性"
                  value={f.cc_sc ?? '—'}
                  opts={CC_SC_OPTS}
                  readOnly={ro}
                  onCommit={(v) => {
                    const next = v === '—' ? null : v;
                    if (next !== (f.cc_sc ?? null)) onPatch({ cc_sc: next });
                  }} />
      </div>

      {/* 第 2 行：尺寸公差（上下偏差）。形位公差行的公差由「公差带」决定，
          上下偏差对它不适用 —— 整行不渲染（v0.31.4 起不再挂「本行是形位公差…」这类说明）。 */}
      {!gdtOn && (
        <div className="fx-line">
          <FxText label="上偏差" type="number" width={84} value={numStr(f.upper_dev)} readOnly={ro}
                  title="相对名义值的带符号偏差，如 +0.2、-0.1；留空 = 0（单边公差只填一侧）"
                  onCommit={(v) => onNum('upper_dev', f.upper_dev ?? null, v, true)} />
          <FxText label="下偏差" type="number" width={84} value={numStr(f.lower_dev)} readOnly={ro}
                  title="相对名义值的带符号偏差，如 -0.1；留空 = 0（单边公差只填一侧）"
                  onCommit={(v) => onNum('lower_dev', f.lower_dev ?? null, v, true)} />
          <FxField label="公差列将显示">
            <span className="fx-ro fx-preview">{tolPreview}</span>
          </FxField>
          <span className="fx-tip fx-tip-wide">
            留空那一侧按 0 算 —— 单边公差只填一侧即可：上偏差留空 + 下偏差 -0.1 → <b>0/-0.1</b>；
            上下相等反向（+0.1 / -0.1）自动写成 <b>±0.1</b>。改完失焦即保存，公差列同步跟着变。
            {uni ? `（当前：${uni}）` : ''}
          </span>
        </div>
      )}

      {/* 第 3 行：形位公差（v0.31.3 新增）。勾选即把本行标为 GD&T —— 识别常把
          形位公差框读成普通尺寸，之前只能删了重建，现在就地改性质。 */}
      <div className="fx-line fx-gdt-line">
        <FxCheck label="形位公差" checked={gdtOn} readOnly={ro}
                 onCommit={(v) => {
                   if (v === gdtOn) return;
                   // 转 GD&T 时若还没有公差带，用原上偏差顶上 —— 解析器当初就是
                   // 这么塞的（upper_dev = zone），不顶一下公差列会瞬间变空。
                   if (v) {
                     const patch: Record<string, any> = { kind: 'gdt' };
                     if (f.gdt?.zone == null && f.upper_dev != null) {
                       patch.zone = f.upper_dev;
                     }
                     onPatch(patch);
                   } else {
                     onPatch({ kind: 'dimension' });
                   }
                 }} />
        {gdtOn ? (
          <>
            <FxSymbolSelect value={sym} readOnly={ro}
                            onCommit={(v) => {
                              if (v === (f.gdt?.symbol ?? null)) return;
                              const patch: Record<string, any> = { symbol: v };
                              // v0.36 / L3-①：符号是**结构化选择**，名称必须跟着走。
                              // 只发 symbol 的话 `features.type` 会停在识别当时的错值上
                              // （实测 ce#189：平面度被识别成位置度，改完符号后 type
                              // 仍是 position）⇒ 人工修正攒不下来，闭环样本没标签。
                              // 名称取**选择器本表**的中文名（前端单一出处），后缀
                              // （如 `(CZ)`）沿用原值，别把人工标注的修饰符抹掉。
                              // 后端 `gdt_type_from_name()` 据此回写 `type`。
                              if (v) patch.characteristic = withModifierSuffix(symbolToZh(v), f.characteristic);
                              onPatch(patch);
                            }} />
            <FxText label="公差带" type="number" width={74} value={numStr(f.gdt?.zone)} readOnly={ro}
                    title="特征控制框里的公差值（可带 ⌀）。公差列显示的就是它"
                    onCommit={(v) => onNum('zone', f.gdt?.zone ?? null, v, true)} />
            {/* 非对称公差带（ASME Y14.5-2018 §11.3.1.2）：图纸写法 `0.6 UZ 0.4`，
                前面的 0.6 是「公差带」，UZ 后面的 0.4 就是这里要填的偏移量。
                可正可负（实测有 `0.1 UZ -0.05`）；留空 = 对称公差带。 */}
            <FxText label="非对称 UZ" type="number" width={74} value={numStr(f.gdt?.uz_offset)}
                    readOnly={ro}
                    title="ASME Y14.5-2018 §11.3.1.2 非对称公差带：填图纸上 UZ 后面的那个数（可正可负）。留空 = 对称公差带。例：公差带 0.6 + 非对称 UZ 0.4 → 公差列显示 0.6UZ0.4"
                    onCommit={(v) => onNum('uz_offset', f.gdt?.uz_offset ?? null, v, true)} />
            <FxCheck label="Ø 直径" checked={!!f.gdt?.zone_is_diameter} readOnly={ro}
                     onCommit={(v) => onPatch({ zone_is_diameter: v })} />
            <DatumEditor datums={f.gdt?.datums ?? []} symbol={sym} readOnly={ro}
                         onCommit={(d) => onPatch({ datums: d })} />
            <FxField label="材料条件">
              {ro ? (
                <span className="cell-ro fx-ro">{materialLabel}</span>
              ) : (
                <select className="cell-input fx-in" value={f.gdt?.material_condition ?? ''}
                        title={symbolAllowsMmc(sym)
                          ? '公差值 / 基准后的实体条件修饰符（Y14.5-2018 图 6-12）'
                          : '该符号按标准不适用实体条件修饰符（形状公差），请核对图纸'}
                        onChange={(e) => onPatch({ material_condition: e.target.value || null })}>
                  {MATERIAL_OPTS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
                </select>
              )}
            </FxField>
            {sym ? (
              <span className="fx-tip">
                {symbolGlyph(sym)} {symbolToZh(sym)}
                {symbolAllowsDatum(sym) ? '' : '：形状公差，标准上不引用基准'}
              </span>
            ) : (
              <span className="fx-tip fx-tip-warn">
                ⚠ 符号未识别 —— 图纸上符号格是一个图形，OCR 读不到字符，系统不会凭空猜类型。
                请在上面「符号」里选图纸上真实印的那个符号（选完会自动记住该字体的码位）。
              </span>
            )}
            <span className="fx-tip fx-tip-wide">
              公差列将显示 <b>{tolPreview}</b>。
              「非对称 UZ」填图纸 <b>0.6 UZ 0.4</b> 里 UZ 后面那个数（可正可负），
              它是公差带往材料外一侧的偏移量；留空即为对称公差带。
            </span>
          </>
        ) : null}
      </div>

      {/* 第 4 行：备注（用户要求挪到最后）+ 唯一破坏性操作。
          不再有「状态四态 / 保存」按钮 —— 字段全部改动即生效，
          状态下拉由编号徽标（单击循环）与批量条承担。 */}
      <div className="fx-line fx-line-last">
        <FxText label="备注" width={300} value={f.note ?? ''} placeholder="如：电镀后测量"
                readOnly={ro}
                onCommit={(v) => { if (v !== (f.note ?? '')) onPatch({ note: v }); }} />
        <span className="fx-tip">
          {ro ? '只读角色：本行字段不可编辑' : '改动即时生效（失焦即保存）· 状态点编号徽标切换'}
        </span>
        <span className="fx-spacer" />
        {/* v0.47：删除是破坏性写操作 ⇒ 只读角色**连按钮都不给**。
            （后端 `DELETE /api/drawing/{id}/feature/{pk}` 要求 engineer，会拦；
              这里不画是为了让"只读"看起来就是只读。） */}
        {!ro && (
          <button type="button" className="fx-del" disabled={busy}
                  title="删除该特征（气泡一并移除，其余编号自动补位；可按 Ctrl+Z 撤销）"
                  onClick={onDelete}>删除</button>
        )}
      </div>
    </div>
  );
}

// ================================================================ 主组件
export function FeatureTable({ drawingId, features, onChanged, readOnly = false }: Props) {
  const { showToast, selectedFeaturePk, selectFeature } = useUI();
  // 只读角色的**唯一收口**：所有编辑入口（行内直编 / 徽标切状态 / 展开行 / 批量）
  // 都从这里派生 `ro` 判断，而不是各处各判一次 —— 漏判一处就是一个能改数据的口子。
  const ro = readOnly;
  const [filter, setFilter] = useState<FilterKey>('all');
  // 多选批量：勾选的特征主键集合（用数组便于稳定序列化）
  const [checkedPks, setCheckedPks] = useState<number[]>([]);
  const checkedSet = useMemo(() => new Set(checkedPks), [checkedPks]);
  // 展开行 / 行内编辑器都只允许一个：展开行是低频字段的家，同时开多行会把表格撑得
  // 很长，反而比原来的底部面板更难用。
  const [expandedPk, setExpandedPk] = useState<number | null>(null);
  const [editing, setEditing] = useState<{ pk: number; key: EditKey } | null>(null);

  // 切图纸：勾选 / 筛选 / 展开 / 编辑全部复位（否则上一张图的 pk 会带过来）
  useEffect(() => {
    setCheckedPks([]);
    setFilter('all');
    setExpandedPk(null);
    setEditing(null);
  }, [drawingId]);

  const statusMut = useMutation({
    mutationFn: (p: { pk: number; status: FeatureStatus }) =>
      setFeatureStatus(drawingId, p.pk, p.status),
    onSuccess: (f) => { showToast(`#${f.balloon_no} → ${STATUS_CN[f.status]}`, 'success'); onChanged(); },
    onError: (e: any) => showToast(`状态切换失败：${e.message}`, 'error'),
  });

  // 行内直编与展开行共用：单字段 PATCH。后端对 EDITABLE_FIELDS 里的字段会自动置 MODIFIED。
  // （注意是后端的 EDITABLE_FIELDS；types/index.ts 里那个同名 `EDIT_KEYS` 无人引用。）
  const patchMut = useMutation({
    mutationFn: (p: { pk: number; patch: Record<string, any> }) =>
      patchFeature(drawingId, p.pk, p.patch),
    onSuccess: (f) => { showToast(`已保存 #${f.balloon_no}`, 'success'); onChanged(); },
    onError: (e: any) => showToast(`保存失败：${e.message}`, 'error'),
  });

  // v0.23：真正删除特征（连同气泡），后端会重排 balloon_no。
  // 2026-09-16：按用户要求**去掉二次确认**（按了就删）。兜底 = 提示条上的
  // 「撤销」按钮 + 全局 Ctrl+Z；后端撤销栈能把特征放回原编号位置。
  const { remove: removeFeature, removeMany: removeFeatures,
          busy: deleteBusy } = useFeatureDelete(drawingId, (pk) => {
    // 删掉的正好是当前选中项 / 正在编辑项 / 正在展开项 → 一并收起
    if (selectedFeaturePk === pk) selectFeature(null);
    setExpandedPk((p) => (p === pk ? null : p));
    setEditing((p) => (p?.pk === pk ? null : p));
  });

  // ---------------------------------------------------------------- 多选批量操作
  const batchStatusMut = useMutation({
    mutationFn: (p: { pks: number[]; status: FeatureStatus }) =>
      batchSetFeatureStatus(drawingId, p.pks, p.status),
    onSuccess: (res) => {
      const miss = res.missing.length ? `，跳过 ${res.missing.length} 个已失效项` : '';
      showToast(`${res.updated} 项已置为「${STATUS_CN[res.status]}」${miss}`, 'success');
      setCheckedPks([]);
      onChanged();
    },
    onError: (e: any) => showToast(`批量操作失败：${e.message}`, 'error'),
  });
  const batchBusy = batchStatusMut.isPending || deleteBusy;

  // ------------------------------------------------ v0.37「合并为一条」（P2 正样本）
  // 场景：一个真实标注被 OCR / 解析切成了两段（`1` + `6`），表格里成了两条。
  // 工程师多选后点「⛓ 合并为一条」：保留阅读顺序最靠前的那条、其余按删除处理。
  //
  // 为什么要把**合并后的文本**呈上来让人改，而不是后端直接拼：
  // 合并结果就是「切分规则」的训练正样本，拼错了比没有样本更糟 —— 会教出错误规则。
  // 因此预填只是起点，工程师必须确认/修正后再提交。
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeText, setMergeText] = useState('');

  /** 选中项按**阅读顺序**排好 —— `seq` 就是图纸顺序（建行时 seq = balloon_no - 1），
   *  缺 seq 的排最后并用 id 兜底，保证顺序稳定可复现。 */
  const mergeTargets = useMemo(() => {
    return features
      .filter((f) => checkedSet.has(f.id))
      .sort((a, b) => (a.seq ?? Number.MAX_SAFE_INTEGER) - (b.seq ?? Number.MAX_SAFE_INTEGER)
        || a.id - b.id);
  }, [features, checkedSet]);

  /** 该段的「原文」：优先 display_text（识别原始串），退回界面上的要求列文本。 */
  const pieceText = (f: Feature): string =>
    (f.display_text || '').trim() || reqText(f);

  /** 选中项里有没有 GD&T 行 —— 后端**不合并 gdt_features 子表**（见 merge_features
   *  docstring 的"不做的事"）。这里只提示，不阻断：拦死会让工程师无从下手，
   *  而提示能让他在提交前把 GD&T 行取消掉。 */
  const mergeHasGdt = useMemo(
    () => mergeTargets.some((f) => f.kind === 'gdt'), [mergeTargets]);

  const openMerge = () => {
    // 防御式降级：选中项可能在打开对话框之前就被别处删掉了（勾选状态是主键快照）。
    if (mergeTargets.length < 2) {
      showToast('可合并的行不足 2 条 —— 选中的行可能已被删除，请重新勾选', 'error');
      setCheckedPks([]);
      return;
    }
    setMergeText(mergeTargets.map(pieceText).join(''));
    setMergeOpen(true);
  };

  const mergeMut = useMutation({
    mutationFn: (p: { pks: number[]; text: string }) =>
      mergeFeatures(drawingId, p.pks, p.text),
    onSuccess: (res) => {
      const miss = res.missing.length ? `，跳过 ${res.missing.length} 个已失效项` : '';
      const warn = res.event_recorded ? '' : '（⚠ 样本未记录，请反馈）';
      showToast(`已合并 ${res.merged} 条 → #${res.kept.balloon_no}${miss}${warn}`, 'success');
      setMergeOpen(false);
      setMergeText('');
      setCheckedPks([]);
      onChanged();
    },
    onError: (e: any) => {
      // 防御式降级：后端若是 v0.37 之前的版本，这个接口不存在（404/405），
      // 直译 axios 的报错会显示成"Request failed with status code 404"，
      // 工程师看不出发生了什么 —— 这里翻成人话，并说明数据未被改动。
      const code = e?.response?.status;
      const detail = e?.response?.data?.detail;
      const msg = code === 404 || code === 405
        ? '当前后端不支持「合并为一条」（接口未上线）—— 数据未改动，请先升级后端'
        : code === 422
          ? `无法合并：${detail ?? e.message}`
          : `${e.message}（数据可能未被改动，请刷新确认）`;
      showToast(`合并失败：${msg}`, 'error');
    },
  });

  const deleteChecked = () => {
    if (!checkedPks.length) return;
    const pks = checkedPks;
    setCheckedPks([]);
    removeFeatures(pks);
  };

  // ---------------------------------------------------------------- 派生数据
  const filtered = useMemo(() => {
    if (filter === 'all') return features;
    if (filter === 'review') {
      return features.filter((f) => (f.filter_status ?? 'ACCEPT') === 'REVIEW');
    }
    return features.filter((f) => f.status === filter);
  }, [features, filter]);

  const reviewCount = useMemo(
    () => features.filter((f) => (f.filter_status ?? 'ACCEPT') === 'REVIEW').length,
    [features]);
  const statusCount = useMemo(() => {
    const m: Record<string, number> = {};
    for (const f of features) m[f.status] = (m[f.status] ?? 0) + 1;
    return m;
  }, [features]);

  // 勾选集合里若含已不存在于本图的 pk（如刚被删除），静默剔除
  useEffect(() => {
    setCheckedPks((prev) => {
      const valid = prev.filter((pk) => features.some((f) => f.id === pk));
      return valid.length === prev.length ? prev : valid;
    });
  }, [features]);

  const visiblePks = filtered.map((f) => f.id);
  const allChecked = visiblePks.length > 0 && visiblePks.every((pk) => checkedSet.has(pk));
  const someChecked = !allChecked && visiblePks.some((pk) => checkedSet.has(pk));
  const toggleAll = () => {
    if (allChecked) {
      setCheckedPks((prev) => prev.filter((pk) => !visiblePks.includes(pk)));
    } else {
      setCheckedPks((prev) => Array.from(new Set([...prev, ...visiblePks])));
    }
  };
  const toggleOne = (pk: number) => setCheckedPks((prev) =>
    prev.includes(pk) ? prev.filter((x) => x !== pk) : [...prev, pk]);

  // ---------------------------------------------------------------- ↑ / ↓ 切换气泡
  // 用户要求：「特征栏那里能不能用上下键来控制气泡图」→ ↑/↓ 在**当前可见行**之间移动选中项，
  // 画布的居中 effect 会跟着把对应气球拉到视口中央（与点击表格行完全同一条链路）。
  //
  // 三个必须做的判断：
  //   ① 输入控件里不抢 —— 数字框里 ↑/↓ 是调数值、下拉里是选项切换（否则改公差时会把选择跳走）；
  //   ② 带修饰键不劫持（Ctrl/⌘/Alt+↑ 留给系统与浏览器）；
  //   ③ preventDefault —— 否则表格滚动容器自己会先滚一段，再叠上 scrollIntoView，位置会抖。
  // 监听装在 window 上（和画布一致）：用户点完某行焦点就在 body 上，装在表格上会收不到键。
  // 通过 ref 读最新状态，effect 只装一次（依赖为空），避免每次选择变化都重绑监听。
  const navRef = useRef<{ list: number[]; cur: number | null }>({ list: [], cur: null });
  navRef.current = { list: visiblePks, cur: selectedFeaturePk };
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      if (e.ctrlKey || e.metaKey || e.altKey || e.shiftKey) return;
      if (isTypingTarget(e.target)) return;
      const { list, cur } = navRef.current;
      if (list.length === 0) return;
      const i = cur == null ? -1 : list.indexOf(cur);
      // 选中项不在当前筛选结果里（刚切了筛选）时：↓ 从头开始、↑ 从尾开始
      const next = e.key === 'ArrowDown'
        ? (i < 0 ? list[0] : list[Math.min(i + 1, list.length - 1)])
        : (i < 0 ? list[list.length - 1] : list[Math.max(i - 1, 0)]);
      e.preventDefault();
      if (next === cur) return;          // 走到头了：吃掉按键，但不动（也别让页面滚）
      selectFeature(next);
      // v0.76：**不再跟着展开**。旧写法 `setExpandedPk(next)` 让每按一次 ↑↓ 就
      // 展开/收起一行（约 200px），表格整体上下抽动 —— 用户说的「点了就跳跑掉」
      // 有一半来自这里。展开改由行尾 ▸ 显式触发（见 `.fc-toggle`）。
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [selectFeature]);

  // ---------------------------------------------------------------- 文本渲染
  // 「公差」一列显示什么由 lib/annot.ts 的 toleranceDisplay() 单点决定 ——
  // 结构化偏差优先，tolerance_text 退为兜底。以前这里另写一份（tolerance_text 排最前），
  // 就是 v0.31.3 用户反馈「改偏差、公差列不动」的根因。
  const reqText = (f: Feature): string => {
    if (f.kind === 'gdt') {
      return f.gdt?.zone != null
        ? `${f.gdt.zone_is_diameter ? 'Ø' : ''}${fmtNum(f.gdt.zone)}`
        : (f.display || '—');
    }
    if (f.nominal != null) return `${f.is_diameter ? 'Ø' : ''}${f.nominal}${f.unit === '°' ? '°' : ''}`;
    return f.display || '—';
  };
  /**
   * 「要求」列的**显示**口径：`4×5`（数量 × 名义值）。
   *
   * ★ 只用于渲染 / 悬停提示，**编辑框里放的仍是 `reqText()`（纯名义值）** ——
   *   否则双击时会把合成串 `4×5` 填进输入框，用户随口改个别处就失焦回写，
   *   等于把显示口径灌进存储。这与「公差列显示派生值、但编辑走上下偏差」是同一条纪律。
   * ★ 带数量前缀的条件是 `qty > 1`（库里的默认值就是 1），GD&T 行若真有 4 处也照样显示。
   */
  const reqDisplay = (f: Feature): string => {
    const base = reqText(f);
    return f.qty > 1 && base !== '—' ? `${f.qty}×${base}` : base;
  };
  const tolText = (f: Feature): string => toleranceDisplay(f);
  /** 公差是否由结构化上下偏差派生（派生值加标记，提示「改这里没用，去展开行改偏差」）。 */
  const tolDerived = (f: Feature): boolean =>
    f.kind !== 'gdt' && (f.upper_dev != null || f.lower_dev != null);
  const rowTitle = (f: Feature): string => {
    const parts = [
      `编号 #${f.balloon_no}`,
      `特征ID ${f.feature_id}`,
      `${f.kind === 'gdt' ? 'GD&T' : f.kind} / ${f.type}`,
      `状态：${STATUS_CN[f.status]}`,
      // 公差是派生的还是直接存的，悬停就能看清 —— 免得又去公差格上死磕
      `要求：${reqDisplay(f)}　公差：${tolText(f)}`
        + (tolDerived(f) ? '（派生自上下偏差）' : ''),
    ];
    // 图纸原文（`4X R5.0`）—— 数量前缀、「未注」公差这些都在原文里，
    // 是核对结构化值对不对的唯一依据，所以悬停就给出来。
    if (f.display_text && f.display_text !== reqDisplay(f)) {
      parts.push(`图纸原文：${f.display_text}`);
    }
    if ((f.filter_status ?? 'ACCEPT') === 'REVIEW') {
      parts.push(`⚠ 待复核：${f.filter_reason ?? '需人工确认'}`);
    }
    if (f.note) parts.push(`备注：${f.note}`);
    return parts.join('\n');
  };

  // ---------------------------------------------------------------- 提交
  /** 数值字段：空串 → null（可选时）；非数字给提示且不提交。 */
  const commitNum = (f: Feature, field: string, cur: number | null, raw: string) => {
    const t = raw.trim();
    if (t === '') {
      if (cur === null) return;
      patchMut.mutate({ pk: f.id, patch: { [field]: null } });
      return;
    }
    const n = Number(t);
    if (!Number.isFinite(n)) { showToast(`「${field}」需要数字`, 'error'); return; }
    if (cur === n) return;
    patchMut.mutate({ pk: f.id, patch: { [field]: n } });
  };

  /** 行内单元格的落点：name → characteristic；req → nominal（GD&T → zone）；tol → 见内。
   *
   * v0.31.3 起「公差」单元格不再是「写 tolerance_text 就完事」：
   *   ① GD&T 行 → 写 **zone**（公差列显示的就是公差带，以前写文本等于改完看不见）；
   *   ② 尺寸行 → 先尝试把输入**解析成上下偏差**（`parseDev`），能解析就写结构化偏差 ——
   *      这样 C/D 上下限、Pass/Fail 判定、快照都跟着对；解析不出且本行已有偏差时
   *      **拒绝写入并说明原因**（否则写了也看不见，又是一次「改了没反应」）；
   *   ③ 清空只清「文本公差」，**不动结构化偏差** —— 防止点一下输入框再失焦就把偏差清了。
   *   ④ 改偏差**不覆盖 tolerance_text**：那是识别原始文本，是修正事件「AI 原来给的错在哪」
   *      的唯一证据，覆盖了学习环就瞎了。
   */
  const commitCell = (f: Feature, key: EditKey, raw: string) => {
    setEditing(null);
    if (ro) return;                          // 只读角色：连提交都不做（见 `ro` 的说明）
    const t = raw.trim();
    if (key === 'name') {
      // 比对与写入都走**显示口径**（characteristicZh）：库里存的是英文旧值时，
      // 输入框里给用户看的是「长度」；他原样点出来就不该产生一次写库 ——
      // 拿中文去和库里的 `Length` 比会不等，于是每次点开再确认都白写一次。
      if (t === '' || t === characteristicZh(f.characteristic)) return;
      patchMut.mutate({ pk: f.id, patch: { characteristic: t } });
      return;
    }
    if (key === 'tol') {
      if (f.kind === 'gdt') { commitNum(f, 'zone', f.gdt?.zone ?? null, raw); return; }
      if (t === '') {
        if (!f.tolerance_text) return;
        patchMut.mutate({ pk: f.id, patch: { tolerance_text: null } });
        return;
      }
      const parsed = parseDev(t);
      if (parsed) {
        if (parsed.u === f.upper_dev && parsed.l === f.lower_dev) return;
        patchMut.mutate({ pk: f.id, patch: { upper_dev: parsed.u, lower_dev: parsed.l } });
        return;
      }
      if (t === (f.tolerance_text ?? '')) return;
      if (f.upper_dev != null || f.lower_dev != null) {
        showToast(`「${t}」不是数值偏差：本行公差由上下偏差派生（${tolText(f)}），`
                  + '要写文字公差请先在展开行清空上下偏差', 'error');
        return;
      }
      patchMut.mutate({ pk: f.id, patch: { tolerance_text: t } });
      return;
    }
    // 「要求」列：先试**数量写法**（`4*R5` / `4X5` / `4×R5`）—— 一次落 `qty` + 名义值。
    // 数量本来就有家（`features.qty`，图纸 `4X R5.0` 里的 4），这里只是给它一个入口。
    const qn = parseQtyNominal(raw);
    if (qn) {
      const sameVal = f.kind === 'gdt'
        ? (f.gdt?.zone ?? null) === qn.nominal
        : f.nominal === qn.nominal;
      if (sameVal && qn.qty === f.qty) return;   // 原样点出来不该产生一次写库
      patchMut.mutate({
        pk: f.id,
        patch: f.kind === 'gdt'
          ? { qty: qn.qty, zone: qn.nominal }
          : { qty: qn.qty, nominal: qn.nominal },
      });
      return;
    }
    // 带数量分隔符但没解析出来（`4*` / `0X5` / `4*R`）—— 别让人对着
    // 「「nominal」需要数字」这种内部字段名发懵，直接告诉他能敲成什么样。
    if (/[*xX×]/.test(t)) {
      showToast('数量写法支持 4*R5 / 4X5 / 4×R5 —— 数量要是 ≥1 的整数，例如 4*R5', 'error');
      return;
    }
    if (f.kind === 'gdt') commitNum(f, 'zone', f.gdt?.zone ?? null, raw);
    else commitNum(f, 'nominal', f.nominal ?? null, raw);
  };

  const editInitial = (f: Feature, key: EditKey): string => {
    if (key === 'name') return characteristicZh(f.characteristic ?? '');
    if (key === 'tol') {
      // GD&T 行编辑的是公差带；尺寸行编辑的是「文本公差」（派生值只做占位提示）
      if (f.kind === 'gdt') return numStr(f.gdt?.zone);
      return f.tolerance_text ?? '';
    }
    if (f.kind === 'gdt') return numStr(f.gdt?.zone);
    return numStr(f.nominal);
  };
  // 公差通常是「派生的」（来自 upper_dev/lower_dev），直接塞进输入框会让人以为
  // 已经存过；改成占位符展示，用户真要覆盖时再输入。
  const editPlaceholder = (f: Feature, key: EditKey): string => {
    if (key !== 'tol') return '';
    const d = tolText(f);
    return d === '—' ? '' : d;
  };

  return (
    <div className="feature-table-wrap">
      <div className="ft-toolbar">
        <span className="ft-title">特征表 <em>({features.length})</em>
          <span className="ft-hint">
            {ro ? '只读角色：可查看与导出，不能修改'
                : '单击选中行 · 双击单元格改值 · 行尾 ▸ 展开字段 · ↑↓ 切换气泡'}
          </span>
          {reviewCount > 0 && (
            <span className="fs-review-count"
                  title="候选过滤判定为 REVIEW，需人工确认是否生成气泡（界面以黄色虚线气泡标示）"
                  style={{ color: '#f9a825', marginLeft: 6, fontSize: 12, fontWeight: 600 }}>
              ⚠ 待复核 {reviewCount}
            </span>
          )}
        </span>
        <div className="ft-right">
          <select className={`ft-filter-select${filter !== 'all' ? ' on' : ''}`}
                  value={filter}
                  title="按状态筛选特征行"
                  onChange={(e) => setFilter(e.target.value as FilterKey)}>
            <option value="all">全部（{features.length}）</option>
            {STATUS_ORDER.map((s) => (
              <option key={s} value={s}>{STATUS_CN[s]}（{statusCount[s] ?? 0}）</option>
            ))}
            {reviewCount > 0 && (
              <option value="review">仅待复核（{reviewCount}）</option>
            )}
          </select>
        </div>
      </div>

      {/* 多选批量条：勾选任意行后浮出，一键确认 / 排除 / 删除。
          v0.47：只读角色整条不渲染（勾选框本身也没了）。 */}
      {!ro && checkedPks.length > 0 && (
        <div className="ft-batchbar">
          <span className="fb-count">已选 <b>{checkedPks.length}</b> 项</span>
          <button className="btn sm fb-ok" disabled={batchBusy}
                  title="把选中项一键置为「已确认」，纳入报告"
                  onClick={() => batchStatusMut.mutate({ pks: checkedPks, status: 'CONFIRMED' })}>
            ✓ 一键确认
          </button>
          <button className="btn sm fb-rjt" disabled={batchBusy}
                  title="把选中项一键置为「异常」——保留记录但在报告中跳过"
                  onClick={() => batchStatusMut.mutate({ pks: checkedPks, status: 'REJECTED' })}>
            ⊘ 一键排除
          </button>
          <button className="btn sm fb-del" disabled={batchBusy}
                  title="删除选中项（气泡一并移除，其余编号自动补位；可按 Ctrl+Z 撤销）"
                  onClick={deleteChecked}>
            ✖ 删除选中
          </button>
          {/* ⛓ 合并为一条：仅当选中 ≥2 条时可用（1 条无从合起）。
              与「删除 + 手动添加」的区别：这里保留最前一条并**记录切分错误信号**，
              是 v0.37「P2 形态 B」的训练正样本来源。 */}
          <button className="btn sm fb-merge" disabled={batchBusy || checkedPks.length < 2}
                  title={checkedPks.length < 2
                    ? '至少选中 2 条才能合并（本行提示：一个标注被切成了几段）'
                    : '把被错误切分的多条合并为一条（保留第一条，其余按删除处理，可撤销）'}
                  onClick={openMerge}>
            ⛓ 合并为一条
          </button>
          <button className="btn sm" disabled={batchBusy}
                  onClick={() => setCheckedPks([])}>清除选择</button>
          {batchBusy && <span className="fb-busy">⏳ 处理中…</span>}
        </div>
      )}

      {/* 「合并为一条」对话框（v0.37）。刻意做成**必确认**：合并文本 = 切分规则的
          训练正样本，拼错比没有更糟，所以预填只是起点，需要人看一眼再提交。 */}
      {mergeOpen && (
        <div className="ft-merge-layer">
          <div className="modal-backdrop" onClick={mergeMut.isPending ? undefined : () => setMergeOpen(false)} />
          <div className="modal ft-merge-modal" role="dialog" aria-modal="true">
            <div className="modal-head">
              <div>
                <div className="modal-title">⛓ 合并为一条</div>
                <div className="modal-sub">
                  这一操作表示：<b>下列几条在图纸上本是一个标注</b>，被识别切成了多段。
                </div>
              </div>
            </div>
            <div className="modal-body">
              <div className="fm-pieces">
                {mergeTargets.map((f, i) => (
                  <div className="fm-piece" key={f.id}>
                    <span className="fm-idx">{i + 1}</span>
                    <span className="fm-no">#{f.balloon_no}</span>
                    <span className="fm-txt">{pieceText(f) || '（空）'}</span>
                  </div>
                ))}
              </div>
              <label className="fm-label" htmlFor="fm-text">合并后的标注（可直接编辑）</label>
              <textarea id="fm-text" className="fm-textarea" rows={2}
                        value={mergeText}
                        disabled={mergeMut.isPending}
                        placeholder="如：16"
                        onChange={(e) => setMergeText(e.target.value)} />
              <div className="modal-hint">
                保留 <b>#{mergeTargets[0]?.balloon_no ?? '—'}</b>（阅读顺序最前），其余删除；
                编号自动补位，可按 <b>Ctrl+Z</b> 撤销（撤销只会把被删的几条放回来，
                <b>这一条的值不还原</b>）。
                <br />
                ⚠ 只做「拼接」，<b>不改标称值 / 上下偏差</b> —— 合并完再按需编辑那些字段。
                {mergeTargets.length > 2 && (
                  <> <br />⚠ 已选中 {mergeTargets.length} 条，请核对上面的顺序与拼接结果。</>
                )}
                {mergeHasGdt && (
                  <> <br />⚠ 选中项里有 <b>GD&amp;T 行</b>：本操作只合并文本，
                    <b>不会合并 GD&amp;T 子表</b>（符号 / 公差带 / 基准）。
                    建议先取消勾选那些行再合并。</>
                )}
              </div>
            </div>
            <div className="modal-foot">
              <button className="btn" disabled={mergeMut.isPending}
                      onClick={() => setMergeOpen(false)}>取消</button>
              <button className="btn primary" disabled={mergeMut.isPending || !mergeText.trim()}
                      onClick={() => mergeMut.mutate({
                        pks: mergeTargets.map((f) => f.id),
                        text: mergeText,
                      })}>
                {mergeMut.isPending ? '合并中…' : `确认合并 ${mergeTargets.length} 条`}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="feat-table-scroll">
        <table className="feat-table">
          <thead>
            <tr>
              <th className="c-sel">
                {!ro && (
                  <input type="checkbox"
                         className="row-check"
                         checked={allChecked}
                         ref={(el) => { if (el) el.indeterminate = someChecked; }}
                         onChange={toggleAll}
                         title="全选/取消全选当前可见行" />
                )}
              </th>
              <th className="c-feat" title="单击行选中 · 单击编号切换状态 · 双击特征名改名 · 行尾 ▸ 展开字段">编号 · 特征</th>
              <th className="c-num" title="双击即可改；GD&T 行改的是公差带。带数量的图纸标注可直接敲 4*R5 / 4X5 / 4×R5 一次填好（显示成 4×5，数量存在展开行的「数量」里）">要求</th>
              <th className="c-tol" title="尺寸行由「上偏差/下偏差」派生（带 ƒ 标记）——到展开行改偏差；GD&T 行显示公差带。双击即可直编（认 ±0.1 / +0.2/0 / 0/-0.1 这类写法，会自动落到上下偏差）">公差</th>
              {/* 填充列：吸掉面板宽于内容时的剩余宽度。
                  没有它，浏览器会把富余宽度按内容比例摊给各列 → 「特征」列被撑宽，
                  名称与「要求」之间出现一大片空白（用户 v0.31.2 反馈）。
                  宽度全交给它之后，四个数据列各按内容取宽，空白只剩最右边一条，
                  与「列宽跟着内容走」的直觉一致。 */}
              <th className="c-fill" aria-hidden="true" />
            </tr>
          </thead>
          <tbody>
            {filtered.map((f) => {
              const rejected = f.status === 'REJECTED';
              const fsReview = (f.filter_status ?? 'ACCEPT') === 'REVIEW';
              // 形位公差行但符号格没识别出来（图纸上是图形，OCR 无字符）。
              // 从表格里一眼可见「这行还需要你补一个符号」，而不是等点开才发现。
              const symMissing = f.kind === 'gdt' && !f.gdt?.symbol;
              const isSel = selectedFeaturePk === f.id;
              const isChecked = checkedSet.has(f.id);
              // v0.59：徽标颜色**只跟 `status` 走**，REVIEW 改由**虚线边框**表示。
              // 旧写法 `fsReview ? '#f9a825' : STATUS_COLORS[f.status]` 让复核标记
              // 一票否决了状态色 —— 而 REVIEW 用的琥珀 `#f9a825` 与 `AI_FOUND` 的
              // 状态色**完全相同**，加上行内其它待复核判据（⚠ 图钉 / 黄条 / 画布
              // 虚线 / 顶部计数）也全取自 filter_status，于是"改完字段点确定"
              // 在屏幕上真的一像素都不动（用户的原始报障）。
              // 拆开之后两个信号各占一个视觉维度：颜色 = 状态，虚线 = 待复核。
              const badgeColor = STATUS_COLORS[f.status] || '#9e9e9e';
              const isOpen = expandedPk === f.id;
              const editingKey = editing?.pk === f.id ? editing.key : null;
              const startEdit = (key: EditKey) => {
                if (ro) return;                  // 只读角色：不允许进入编辑态
                if (editingKey === key) return;
                setEditing({ pk: f.id, key });
              };
              const nxt = cycleOf(f.status);
              return (
                <Fragment key={f.id}>
                  <tr id={`feat-row-${f.id}`}
                      className={`feat-row ${rejected ? 'rejected' : ''} ${fsReview ? 'fs-review' : ''} ${isSel ? 'selected' : ''} ${isChecked ? 'multi-selected' : ''}`}
                      title={rowTitle(f)}
                      onClick={() => {
                        // v0.76（用户 2026-10-19「手动修改不方便，点击后会跳跑掉」）：
                        // **单击 = 只选中**，不再顺带展开字段。旧写法在同一处
                        // `setExpandedPk(...)` 插入一个约 200px 的展开行，本行以下
                        // 全体下移、滚动条同时变长 ⇒ 手底下的目标换了位置，第二下点空。
                        // 展开现在有两个**显式**入口：行尾 ▸ 按钮 / 双击行空白处。
                        selectFeature(f.id);
                      }}
                      onDoubleClick={(e) => {
                        // 双击行空白处 = 展开/收起（格子与名字里的双击都 stopPropagation 了）
                        e.stopPropagation();
                        setExpandedPk((p) => (p === f.id ? null : f.id));
                      }}>
                    {/* 多选勾选框（不触发行选中）。只读角色不渲染 —— 勾了也用不了。 */}
                    <td className="c-sel" onClick={(e) => e.stopPropagation()}>
                      {!ro && (
                        <input type="checkbox"
                               className="row-check"
                               checked={isChecked}
                               onChange={() => toggleOne(f.id)}
                               title="勾选后可批量确认 / 删除" />
                      )}
                    </td>

                    {/* 编号 · 特征：徽标=状态切换；名称=双击改名（单击由整行接管 → 展开字段） */}
                    <td className="c-feat">
                      <div className="fc-cell">
                        <button type="button"
                                className="balloon-badge fc-badge"
                                style={{ borderColor: badgeColor, color: badgeColor,
                                         borderStyle: fsReview ? 'dashed' : 'solid' }}
                                title={`状态：${STATUS_CN[f.status]} —— ${STATUS_HINT[f.status]}\n单击切到「${STATUS_CN[nxt]}」`
                                       + (fsReview
                                          ? `\n虚线圈 = 候选过滤待人工复核${f.filter_reason ? `：${f.filter_reason}` : ''}`
                                             + '（展开本行可收口）'
                                          : '')}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (ro) return;    // 只读角色：点徽标不改状态
                                  statusMut.mutate({ pk: f.id, status: nxt });
                                }}>
                          {f.balloon_no}
                        </button>
                        {editingKey === 'name' ? (
                          <CellEditor initial={editInitial(f, 'name')}
                                      onCommit={(v) => commitCell(f, 'name', v)}
                                      onCancel={() => setEditing(null)} />
                        ) : (
                          <span className="fc-name"
                                title="双击改名"
                                onDoubleClick={(e) => { e.stopPropagation(); startEdit('name'); }}>
                            {/* 特征名走 annot 的兼容表：库里可能还是识别引擎早期的英文值
                                （`Length`/`Angle`…），界面统一显示中文 */}
                            {characteristicZh(f.characteristic)}
                            {/* 符号走 annot 的字形表：库里可能还是识别引擎的旧码位
                                （如 ⌖ 位置度），直接打印会与标准字形不一致、部分字体还缺字形 */}
                            {f.kind === 'gdt' && f.gdt?.symbol
                              ? ` ${symbolGlyph(f.gdt.symbol)}` : ''}
                          </span>
                        )}
                        {/* 一个行只挂**一个** ⚠（两个都成立时合并到一个 tooltip 里）——
                            不新增 DOM 槽位，列宽算法与前端回归门都不受影响。
                            v0.31.10 起：GD&T 行「符号为空」也算待办 —— 展开行里选一下即可。 */}
                        {(fsReview || symMissing) && (
                          <span className="fc-review"
                                title={[
                                  fsReview
                                    ? `候选过滤待复核：${f.filter_reason ?? '需人工确认'}` : '',
                                  symMissing
                                    ? '符号未识别：展开本行 → 在「符号」里选图纸上印的那个' : '',
                                ].filter(Boolean).join('\n')}>⚠</span>
                        )}
                      </div>
                    </td>

                    {/* 要求 */}
                    {/* v0.76：**只认双击**。旧写法「行已选中时单击即进编辑」把两步压成
                        一步，想点选/滚动时一碰就变输入框；而输入框失焦即提交 ⇒ 容易误改。
                        单击仍有效果 —— 冒泡到整行去做「选中」。 */}
                    <td className="c-num req-cell"
                        onDoubleClick={(e) => { e.stopPropagation(); startEdit('req'); }}>
                      {editingKey === 'req' ? (
                        <CellEditor initial={editInitial(f, 'req')}
                                    onCommit={(v) => commitCell(f, 'req', v)}
                                    onCancel={() => setEditing(null)} />
                      ) : (
                        <span className="cell-ro"
                              title={`${reqDisplay(f)}\n双击可改\n`
                                     + '可敲数量写法：4*R5 / 4X5 / 4×R5（一次填好数量与名义值）'}>
                          {reqDisplay(f)}
                        </span>
                      )}
                    </td>

                    {/* 公差 */}
                    <td className={`c-tol req-cell${tolDerived(f) ? ' derived' : ''}`}
                        onDoubleClick={(e) => { e.stopPropagation(); startEdit('tol'); }}>
                      {editingKey === 'tol' ? (
                        <CellEditor initial={editInitial(f, 'tol')}
                                    placeholder={editPlaceholder(f, 'tol')}
                                    onCommit={(v) => commitCell(f, 'tol', v)}
                                    onCancel={() => setEditing(null)} />
                      ) : (
                        <span className="cell-ro"
                              title={tolDerived(f)
                                ? `${tolText(f)}\n（派生自上下偏差 —— 到展开行改「上偏差/下偏差」）`
                                : `${tolText(f)}\n双击可改`}>
                          {tolText(f)}
                        </span>
                      )}
                    </td>

                    {/* 填充列（说明见表头）：吸走富余宽度 + 行尾展开按钮。
                        按钮用绝对定位挂在右端，**不参与列宽计算**（它的 width 若参与，
                        `.c-fill` 的「把富余宽度全部吸走」就失效了，见上方注释）。 */}
                    <td className="c-fill">
                      <button type="button"
                              className={`fc-toggle${isOpen ? ' open' : ''}`}
                              aria-expanded={isOpen}
                              title={isOpen ? '收起本行字段（双击行空白处也可）' : '展开本行字段：数量/单位/上下偏差/基准/备注…（双击行空白处也可）'}
                              onClick={(e) => {
                                e.stopPropagation();
                                selectFeature(f.id);
                                setExpandedPk((p) => (p === f.id ? null : f.id));
                              }}>
                        {isOpen ? '▾' : '▸'}
                      </button>
                    </td>
                  </tr>

                  {/* 展开行：低频字段的家（不占常驻布局） */}
                  {isOpen && (
                    <tr className="fc-expand">
                      <td colSpan={5}>
                        <ExpandForm f={f}
                                    busy={patchMut.isPending || statusMut.isPending}
                                    readOnly={ro}
                                    onPatch={(patch) => patchMut.mutate({ pk: f.id, patch })}
                                    onNum={(field, cur, raw, allowNull) => {
                                      if (!allowNull && raw.trim() === '') return;
                                      commitNum(f, field, cur, raw);
                                    }}
                                    onDelete={() => removeFeature(f.id)} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {filtered.length === 0 && (
              <tr className="fc-empty">
                <td colSpan={5}>
                  {features.length === 0 ? '暂无特征' : '当前筛选下没有特征'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
